import React, { useRef, useEffect, useState } from 'react';
import { GLASS_SURFACE_STYLE } from './glassSurface';

interface CardProps {
    title?: string | React.ReactNode;
    padding?: string;
    children?: React.ReactNode;
    size?: 'default' | 'small';
    width?: string;
    minWidth?: string;
    direction?: 'row' | 'column';
    align?: 'center';
    dimmed?: boolean;
    fullHeight?: boolean;
    border?: 'base' | 'rounded';
    alignSelf?: 'center';
    height?: string;
    footer?: React.ReactNode;
    glassFooter?: boolean;
    className?: string;
    style?: React.CSSProperties;
    headerConfig?: {
        padding?: string;
    };
}

const Card: React.FC<CardProps> = ({
    title,
    children,
    padding = '20px',
    size = 'default',
    width,
    minWidth,
    dimmed = false,
    direction,
    align,
    fullHeight = false,
    border = 'base',
    alignSelf,
    height,
    footer,
    glassFooter = false,
    className = '',
    style = {},
    headerConfig = {},
}) => {
    const footerRef = useRef<HTMLDivElement>(null);
    const [footerHeight, setFooterHeight] = useState(0);

    useEffect(() => {
        if (glassFooter && footer && footerRef.current) {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setFooterHeight(entry.contentRect.height);
                }
            });

            observer.observe(footerRef.current);
            setFooterHeight(footerRef.current.offsetHeight);

            return () => observer.disconnect();
        }
    }, [glassFooter, footer]);

    // Size-specific padding
    const sizeClasses = size === 'small' ? 'text-sm' : '';

    // Dynamic classes based on props
    const cardClasses = [
        'card-base',
        dimmed ? 'card-dimmed' : '',
        border === 'rounded' ? 'card-rounded' : '',
        fullHeight ? 'h-full' : '',
        height ? 'flex flex-col' : '',
        sizeClasses,
        className,
    ]
        .filter(Boolean)
        .join(' ');

    // Content styles
    const paddingValue = parseInt(padding) || 20;
    const extraPadding = 10;
    const contentStyles: React.CSSProperties = {
        padding,
        display: direction ? 'flex' : undefined,
        flexDirection: direction,
        alignItems: align,
        ...(height && { overflow: 'auto', height: '100%' }),
        ...(glassFooter &&
            footer &&
            footerHeight > 0 && {
                paddingBottom: `${paddingValue + footerHeight + extraPadding}px`,
            }),
    };

    // Card container styles
    const cardContainerStyles: React.CSSProperties = {
        width,
        minWidth,
        alignSelf,
        ...(fullHeight && { height: '100%' }),
        ...(height && { height, display: 'flex', flexDirection: 'column' }),
        ...(glassFooter && footer && { position: 'relative' }),
        overflow: 'hidden',
        ...style,
    };

    return (
        <div className={cardClasses} style={cardContainerStyles}>
            {/* Header */}
            {title && (
                <div
                    className="card-header px-3 py-2"
                    style={{
                        marginBottom: '1px',
                        fontSize: size === 'small' ? '12px' : '13px',
                        fontWeight: 'normal',
                        padding: headerConfig?.padding,
                    }}
                >
                    <div className="font-normal overflow-visible">{title}</div>
                </div>
            )}

            {/* Body */}
            <div
                className="flex-1"
                style={{
                    ...contentStyles,
                    ...(glassFooter && footer && { position: 'relative' }),
                }}
            >
                {children}
            </div>

            {/* Footer */}
            {footer && (
                <div
                    ref={glassFooter ? footerRef : undefined}
                    className="card-footer px-4 py-3"
                    style={
                        glassFooter
                            ? {
                                  ...GLASS_SURFACE_STYLE,
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  borderBottomLeftRadius: border === 'rounded' ? '20px' : '12px',
                                  borderBottomRightRadius: border === 'rounded' ? '20px' : '12px',
                              }
                            : undefined
                    }
                >
                    {footer}
                </div>
            )}
        </div>
    );
};

export default Card;
export { Card };
